import type { FilterOptionsState } from '@mui/material';
import { Autocomplete, TextField } from '@mui/material';
import { Control, Controller, FieldError } from 'react-hook-form';
import React, { useState } from 'react';

import { ActionType } from '../../features/status/state/actions.state';
import { StyledTooltip } from '../styles/components/integrations.styles';
import { rgba } from '../styles/core/utils';
import styled from '@emotion/styled';
import { theme } from '../styles/core/theme';
import { tokens } from '../styles/core/tokens';
import { useAuth } from '../../features/auth/state/auth.state';

const MATERIAL_FIELDS = ['partDetail', 'part', 'materialGrade'] as const;
type MaterialField = typeof MATERIAL_FIELDS[number];

interface ParentItem {
   pa: string;
}

interface BaseFieldProps {
   field: string;
   label: string;
   control: Control;
   disabled: boolean;
   actionType?: ActionType;
   materialDiffers?: boolean;
   clientCreated?: boolean;
   parentItem?: ParentItem;
   hiddenCompositionSharePAs?: string[];
   customErrorMessage?: string;
   firstTextFieldRef?: (ref: HTMLInputElement | null) => void;
   index?: number;
   helperText?: string;
}

interface CustomTextFieldProps extends BaseFieldProps {
   triggerValidation?: () => void;
   onChg?: (e: React.ChangeEvent<HTMLInputElement>) => void;
   onBlur?: () => void;
   onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
}

export interface AutoCompleteSection {
   name: string;
   values: string[];
}

interface CustomTextAutoCompleteProps extends BaseFieldProps {
   areAnyBulkEditItemsNotClientCreated?: boolean;
   autoCompleteOptions?: string[];
   autoCompleteSections?: AutoCompleteSection[];
   multiple?: boolean;
   freeSolo?: boolean;
   openState: (open: boolean) => void;
   warning?: string;
   onChange?: (value: string | string[] | null) => void;
   filterOptions?: (options: string[], state: FilterOptionsState<string>) => string[];
   noOptionsText?: string;
}

interface DisabledStateProps {
   field: string;
   disabled: boolean;
   canEdit: boolean;
   actionType?: ActionType;
   materialDiffers: boolean;
   clientCreated: boolean;
   areAnyBulkEditItemsNotClientCreated: boolean;
   parentItem?: ParentItem;
   hiddenCompositionSharePAs?: string[];
}

const getDisabledState = (props: DisabledStateProps): boolean => {
   const {
      field,
      disabled,
      canEdit,
      actionType,
      materialDiffers = false,
      clientCreated = false,
      areAnyBulkEditItemsNotClientCreated = false,
      parentItem,
      hiddenCompositionSharePAs,
   } = props;

   if (disabled) return true;

   if (field === 'compositionShare' && parentItem?.pa && hiddenCompositionSharePAs?.includes(parentItem.pa)) {
      return true;
   }

   if (field === 'id' && (!canEdit || materialDiffers)) {
      return true;
   }

   if (MATERIAL_FIELDS.includes(field as MaterialField)) {
      if (actionType === ActionType.editing && !clientCreated) {
         return true;
      }
      if (actionType === ActionType.bulkEditing && areAnyBulkEditItemsNotClientCreated) {
         return true;
      }
   }

   return false;
};

const getTooltipText = (field: string, canEdit: boolean, materialDiffers = false, clientCreated = false): string => {
   if (field === 'id') {
      if (!canEdit) return 'You need to have access level Admin or Editor to edit this field.';
      if (materialDiffers) return 'There are deviating Material grades in your selection.';
   }

   if (MATERIAL_FIELDS.includes(field as MaterialField) && !clientCreated) {
      return 'You can only edit material information for user created rows.';
   }

   return '';
};

interface FieldErrorDisplayProps {
   error?: FieldError;
   customErrorMessage?: string;
   helperText?: string;
   warning?: string;
   isWarning?: boolean;
}

const FieldErrorDisplay: React.FC<FieldErrorDisplayProps> = ({ error, customErrorMessage, helperText, warning, isWarning }) => {
   if (!error?.message && !customErrorMessage && !helperText && !warning) return null;

   return (
      <StyledHelperText isError={Boolean(error || customErrorMessage)} isWarning={isWarning}>
         {error?.message || customErrorMessage || helperText || warning}
      </StyledHelperText>
   );
};

export const CustomTextField: React.FC<CustomTextFieldProps> = ({
   field,
   label,
   control,
   disabled,
   actionType,
   materialDiffers = false,
   clientCreated = false,
   parentItem,
   hiddenCompositionSharePAs,
   customErrorMessage,
   firstTextFieldRef,
   index,
   triggerValidation,
   onChg,
   onBlur,
   onKeyDown,
   helperText,
}) => {
   const { canEdit } = useAuth();
   const canEditValue = canEdit ?? false;

   const finalDisabled = getDisabledState({
      field,
      disabled,
      canEdit: canEditValue,
      actionType,
      materialDiffers,
      clientCreated,
      areAnyBulkEditItemsNotClientCreated: false,
      parentItem,
      hiddenCompositionSharePAs,
   });

   const tooltipText = finalDisabled ? getTooltipText(field, canEdit, materialDiffers, clientCreated) : '';

   return (
      <Controller
         name={field}
         control={control}
         render={({ field: { onChange, value, onBlur: fieldOnBlur }, fieldState: { error } }) => (
            <StyledTooltip title={tooltipText}>
               <div>
                  <StyledSheetTextField
                     error={Boolean(error || customErrorMessage)}
                     label={label}
                     value={Array.isArray(value) ? value.join(', ') : value ?? ''}
                     disabled={finalDisabled}
                     variant="standard"
                     onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        onChange(e?.target?.value);
                        triggerValidation?.();
                        onChg?.(e);
                     }}
                     onBlur={() => {
                        fieldOnBlur();
                        onBlur?.();
                     }}
                     onKeyDown={onKeyDown}
                     size="small"
                     color="primary"
                     inputRef={(input) => {
                        if (index === 0 && firstTextFieldRef) {
                           firstTextFieldRef(input);
                        }
                     }}
                  />
                  <FieldErrorDisplay error={error} customErrorMessage={customErrorMessage} helperText={helperText} />
               </div>
            </StyledTooltip>
         )}
      />
   );
};

export const CustomTextFieldAutoCompl: React.FC<CustomTextAutoCompleteProps> = ({
   field,
   label,
   control,
   disabled,
   actionType,
   clientCreated = false,
   areAnyBulkEditItemsNotClientCreated = false,
   materialDiffers = false,
   parentItem,
   hiddenCompositionSharePAs,
   autoCompleteOptions = [],
   autoCompleteSections,
   customErrorMessage,
   warning,
   firstTextFieldRef,
   openState,
   index,
   freeSolo,
   multiple,
   onChange,
   filterOptions,
   noOptionsText,
}) => {
   const { canEdit } = useAuth();
   const canEditValue = canEdit ?? false;
   const [localWarning, setLocalWarning] = useState<string>();
   const [inputValue, setInputValue] = useState<string>('');

   const finalDisabled = getDisabledState({
      field,
      disabled,
      canEdit: canEditValue,
      actionType,
      materialDiffers,
      clientCreated,
      areAnyBulkEditItemsNotClientCreated,
      parentItem,
      hiddenCompositionSharePAs,
   });

   const tooltipText = finalDisabled ? getTooltipText(field, canEditValue, materialDiffers, clientCreated) : '';

   const enhancedOptions = React.useMemo(() => {
      const uniqueOptions = new Set<string>();
      const result: Array<string> = [];

      const addOption = (value: string) => {
         if (value && !uniqueOptions.has(value)) {
            result.push(value);
            uniqueOptions.add(value);
         }
      };

      autoCompleteSections?.forEach((section) => section.values.forEach(addOption));
      autoCompleteOptions.forEach(addOption);

      return result;
   }, [autoCompleteOptions, autoCompleteSections]);

   const sectionMap = React.useMemo(
      () => new Map(autoCompleteSections?.flatMap((section) => section.values.map((value) => [value, section.name])) ?? []),
      [autoCompleteSections]
   );

   return (
      <Controller
         name={field}
         control={control}
         render={({ field: { onChange: fieldOnChange, value, onBlur }, fieldState: { error } }) => (
            <StyledTooltip title={tooltipText}>
               <div>
                  <StyledAutoComplete
                     disablePortal
                     options={enhancedOptions}
                     multiple={multiple}
                     freeSolo={freeSolo}
                     selectOnFocus
                     clearOnBlur={false}
                     handleHomeEndKeys
                     autoHighlight
                     value={value || (multiple ? [] : null)}
                     noOptionsText={noOptionsText}
                     disabled={finalDisabled}
                     filterOptions={filterOptions}
                     groupBy={(option) => sectionMap.get(option) || ''}
                     onOpen={() => openState(true)}
                     onClose={() => openState(false)}
                     inputValue={inputValue}
                     onInputChange={(event, newInputValue) => {
                        setInputValue(newInputValue);

                        if (freeSolo && newInputValue && !enhancedOptions.includes(newInputValue)) {
                           setLocalWarning(noOptionsText);
                        } else {
                           setLocalWarning(undefined);
                        }
                     }}
                     onChange={(event, newValue) => {
                        let processedValue: string | string[] | null;

                        if (multiple) {
                           processedValue = Array.isArray(newValue) ? newValue : [];
                        } else {
                           processedValue = newValue === null ? null : String(newValue);
                        }

                        fieldOnChange(processedValue);
                        onChange?.(processedValue);

                        if (!multiple && typeof processedValue === 'string') {
                           setInputValue(processedValue);
                        }
                     }}
                     onBlur={() => {
                        if (freeSolo && inputValue && !value) {
                           const newValue = multiple ? [inputValue] : inputValue;
                           fieldOnChange(newValue);
                           onChange?.(newValue);
                        }
                        onBlur();
                     }}
                     renderInput={(params) => (
                        <StyledSheetTextField
                           {...params}
                           error={Boolean(error || customErrorMessage)}
                           label={label}
                           disabled={finalDisabled}
                           variant="standard"
                           size="small"
                           color="primary"
                           inputRef={(input) => {
                              if (index === 0 && firstTextFieldRef) {
                                 firstTextFieldRef(input);
                              }
                           }}
                        />
                     )}
                  />
                  <FieldErrorDisplay
                     error={error}
                     customErrorMessage={customErrorMessage}
                     warning={localWarning || warning}
                     isWarning={true}
                  />
               </div>
            </StyledTooltip>
         )}
      />
   );
};

const StyledAutoComplete = styled(Autocomplete)`
   .MuiAutocomplete-option[aria-selected='true'] {
      background-color: ${theme.colors.state.success};
   }
` as typeof Autocomplete;

const StyledSheetTextField = styled(TextField)`
   width: 100%;
   margin-bottom: 0;

   & .MuiInputLabel-root {
      font-size: ${tokens.font.size.lg};
      transform-origin: top left;

      &:not(.MuiInputLabel-shrink) {
         transform: translate(0, ${tokens.space.md}) scale(1);
      }

      &.MuiInputLabel-shrink {
         transform: translate(0, -${tokens.space.xs}) scale(0.8);
      }

      &.Mui-disabled {
         color: ${rgba(theme.colors.text.primary, 0.38)};
      }
   }

   & .MuiInput-root {
      font-size: ${tokens.font.size.md};
      margin-top: ${tokens.space.md};

      input {
         padding: ${tokens.space.xs} 0 ${tokens.space.xs};
         height: auto;
      }

      &:before {
         border-bottom: ${tokens.border.thin} ${rgba(theme.colors.text.primary, 0.42)};
      }

      &:after {
         display: none;
      }

      &:hover:not(.Mui-disabled):before {
         border-bottom: ${tokens.border.thin} ${rgba(theme.colors.text.primary, 0.87)};
      }

      &.Mui-disabled {
         &:before {
            border-bottom-style: dotted;
         }
      }
   }

   & label.Mui-focused {
      color: ${theme.colors.text.primary};
   }

   & .MuiFormHelperText-root {
      display: none;
   }

   margin-bottom: ${tokens.space.sm};
`;

const StyledHelperText = styled.div<{ isWarning?: boolean; isError?: boolean }>`
   font-size: ${tokens.font.size.sm};
   margin-top: ${tokens.space.xs};
   margin-bottom: ${tokens.space.xs};
   min-height: 1.2em;
   line-height: ${tokens.font.lineHeight.tight};
   color: ${(props) => {
      if (props.isError) return theme.colors.state.error;
      if (props.isWarning) return theme.colors.state.warning;
      return rgba(theme.colors.text.primary, 0.6);
   }};
   display: flex;
   align-items: flex-start;
   white-space: pre-line;
   word-wrap: break-word;
   overflow-wrap: break-word;

   &:before {
      ${(props) =>
         props.isWarning
            ? `
         content: "⚠️";
         margin-right: ${tokens.space.xs};
         flex-shrink: 0;
      `
            : ''}
   }
`;
